import { ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRouteRef } from '@backstage/core-plugin-api';

import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  CODING_SESSION_DEEP_LINK_PARAM,
  useChatRef,
} from '@spotify/plugin-confidence-chat-react';
import {
  ExternalUrlArtifactContent,
  ExternalUrlArtifactListItem,
  getCodingSessionChildChat,
  getCodingSessionParentChat,
  getCodingSessionUrl,
} from '@spotify/plugin-confidence-projects-react';

import { chatRouteRef, projectChatRouteRef } from '../routes';

const useChatPathBuilder = () => {
  const chatRoute = useRouteRef(chatRouteRef);
  const projectChatRoute = useRouteRef(projectChatRouteRef);

  return useCallback(
    (chatResourceName: string): string | undefined => {
      const parts = chatResourceName.split('/');
      if (
        parts.length === 4 &&
        parts[0] === 'projects' &&
        parts[2] === 'chats'
      ) {
        const [, projectId, , chatId] = parts;
        return (
          projectChatRoute?.({ id: projectId, chatId }) ??
          `/projects/${projectId}/chats/${chatId}`
        );
      }
      if (parts.length === 2 && parts[0] === 'chats') {
        const [, chatId] = parts;
        return chatRoute?.({ id: chatId }) ?? `/chats/${chatId}`;
      }
      return undefined;
    },
    [chatRoute, projectChatRoute],
  );
};

const useCodingSessionTranscriptOpener = (
  artifact: AttachmentFragment,
): (() => void) | undefined => {
  const navigate = useNavigate();
  const buildChatPath = useChatPathBuilder();

  const childChatName = getCodingSessionChildChat(artifact);
  const parentChatName = getCodingSessionParentChat(artifact);

  if (!childChatName || !parentChatName) {
    return undefined;
  }

  const path = buildChatPath(parentChatName);
  if (!path) {
    return undefined;
  }

  return () => {
    const [pathname, existingSearch = ''] = path.split('?');
    const params = new URLSearchParams(existingSearch);
    params.set(CODING_SESSION_DEEP_LINK_PARAM, childChatName);
    navigate({ pathname, search: params.toString() });
  };
};

// Prefer the child chat's generated title over the attachment displayName (raw prompt).
const useCodingSessionTitle = (
  artifact: AttachmentFragment,
): string | undefined => {
  const childChatName = getCodingSessionChildChat(artifact);
  const { chat } = useChatRef({
    name: childChatName ?? '',
    skip: !childChatName,
  });
  return chat?.displayName ?? undefined;
};

export const CodingSessionArtifactContent = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => (
  <ExternalUrlArtifactContent
    artifact={artifact}
    href={getCodingSessionUrl(artifact)}
    onOpen={useCodingSessionTranscriptOpener(artifact)}
    title={useCodingSessionTitle(artifact)}
   data-track-source-anchor="7c3f099699;coding-session-artifact-content-external-url"/>
);

export const CodingSessionArtifactListItem = ({
  artifact,
  icon,
  onDelete,
  onRename,
}: {
  artifact: AttachmentFragment;
  icon: ReactNode;
  onDelete?: () => void;
  onRename?: () => void;
}) => (
  <ExternalUrlArtifactListItem
    artifact={artifact}
    icon={icon}
    subtitleLabel="Coding session"
    openLabel="Open coding session"
    href={getCodingSessionUrl(artifact)}
    onOpen={useCodingSessionTranscriptOpener(artifact)}
    title={useCodingSessionTitle(artifact)}
    onDelete={onDelete}
    onRename={onRename}
   data-track-source-anchor="479af2d014;coding-session-artifact-list-item-external-url"/>
);
